<template>
    <div class="card-box m-b-15">
        <div class="contact-card clearfix">
            <router-link :to="{ name: 'leads.show', params: { id: lead.id } }">
                <img class="img-circle pull-left" :src="lead.avatar || `/static/images/avatar.jpg`" :alt="lead.full_name">
            </router-link>
            <div class="member-info clearfix">
                <router-link :to="{ name: 'leads.show', params: { id: lead.id } }">
                    <h4 class="m-t-0 m-b-5 header-title">
                        <b>{{ lead.full_name }}</b>
                        <small class="show m-t-10 t_overflow" v-if="lead.reference">({{ lead.reference }})</small>
                    </h4>
                </router-link>
                <p class="text-muted m-b-5" v-if="lead.country_id">
                    <img :src="`/static/images/flags/${lead.country.acronym}.png`" alt="" class="flag_deal"> {{ lead.country.name }}
                </p>
                <div class="m-b-10 pull-right">
                    <button class="btn btn-primary btn-xs" v-pml-tooltip.bottom-left="$t('lead_research')" @click.prevent="leadResearch" v-pml-block="isResearching" v-if="lead.email"><i class="fa fa-search"></i></button>
                    <router-link :to="{ name: 'leads.edit', params: { id: lead.id } }" class="btn btn-warning btn-xs" v-pml-tooltip.bottom="$t('edit')"><i class="fa fa-pencil"></i></router-link>
                </div>
                <small class="show m-b-10"><strong>{{ $t('created_at') }}: </strong>{{ lead.created_at | pmlDate('DD/MM/YYYY') }}</small>
            </div>
        </div>
        <p v-if="lead.email">
            <i class="m-r-5 fa-fw fa fa-envelope"></i>
            <a :href="mailto" target="_blank">{{ lead.email }}</a>
        </p>
        <div class="row">
            <div class="col-xs-12" v-if="lead.phone">
                <p><i class="m-r-5 fa-fw fa fa-phone"></i> <a :href="`skype:${clearNumber(lead.phone)}?call`">{{ lead.phone }}</a></p>
            </div>
            <div class="col-xs-12" v-if="lead.mobile">
                <p><i class="m-r-5 fa-fw fa fa-mobile"></i> <a :href="`https://wa.me/${clearNumber(lead.mobile)}`" target="_blank">{{ lead.mobile }}</a></p>
            </div>
            <div class="col-xs-12" v-if="lead.dob">
                <p>
                    <i class="m-r-5 fa-fw fa fa-birthday-cake"></i>
                    {{ lead.dob | pmlDate('DD/MM/YYYY') }}
                    <small>({{ lead.dob | pmlCalcAge }})</small>
                </p>
            </div>
            <div class="col-xs-12" v-if="lead.gender">
                <p><i class="m-r-5 fa-fw fa fa-venus-mars"></i> {{ $t(lead.gender) }}</p>
            </div>
        </div>
        <p v-if="lead.comments">
            <strong class="show m-b-5">{{ $t('comments') }}: </strong>
            <span v-html="comments"></span>
        </p>
        <hr>
        <h5>
            <i class="m-r-5 fa-fw fa fa-home"></i> {{ $t('address') }} </h5>
        <label v-if="lead.address">{{ lead.address }}</label>
        <div class="row clearfix">
            <div class="col-xs-12 col-sm-6" v-if="lead.complement">
                <label>{{ lead.complement }}</label><br>
            </div>
            <div class="col-xs-12 col-sm-6" v-if="lead.district">
                <label>{{ lead.district }} </label><br>
            </div>
        </div>
        <div class="row form-group clearfix">
            <div class="col-xs-12 col-sm-6" v-if="lead.city || lead.state">
                <label>{{ lead.city }} <span v-if="lead.state && lead.city">/</span> {{ lead.state }} </label>
            </div>
            <div class="col-xs-12 col-sm-6" v-if="lead.zip_code">
                <label>{{ $t('zip_code') }}: {{ lead.zip_code }}</label>
                <br>
            </div>
        </div>
        <hr>
        <h5>
            <i class="m-r-5 fa-fw fa fa-link"></i> {{ $t('social_media') }} </h5>
        <div class="social_icons">
            <div class="icons" :class="{ 'disabled': !lead.skype }">
                <a :href="skypeLink(lead.skype)" class="btn btn-skype btn-block" v-pml-tooltip="'Skype'">
                    <i class="fa-fw fa fa-skype"></i>
                </a>
            </div>
            <div class="icons" :class="{ 'disabled': !lead.site }">
                <a :href="lead.site" class="btn btn-white btn-block" v-pml-tooltip="'Site'" target="_blank">
                    <i class="fa-fw fa fa-desktop"></i>
                </a>
            </div>
            <div class="icons" :class="{ 'disabled': !lead.linkedin }">
                <a :href="lead.linkedin" class="btn btn-linkedin btn-block" v-pml-tooltip="'LinkedIn'" target="_blank">
                    <i class="fa-fw fa fa-linkedin"></i>
                </a>
            </div>
            <div class="icons" :class="{ 'disabled': !lead.facebook }">
                <a :href="lead.facebook" class="btn btn-facebook btn-block" v-pml-tooltip="'Facebook'" target="_blank">
                    <i class="fa-fw fa fa-facebook"></i>
                </a>
            </div>
            <div class="icons" :class="{ 'disabled': !lead.gplus }">
                <a :href="lead.gplus" class="btn btn-googleplus btn-block" v-pml-tooltip="'Google'" target="_blank">
                    <i class="fa-fw fa fa-google-plus"></i>
                </a>
            </div>
            <div class="icons" :class="{ 'disabled': !lead.instagram }">
                <a :href="lead.instagram" class="btn btn-instagram btn-block" v-pml-tooltip="'Instagram'" target="_blank">
                    <i class="fa-fw fa fa-instagram"></i>
                </a>
            </div>
            <div class="icons" :class="{ 'disabled': !lead.twitter }">
                <a :href="lead.twitter" class="btn btn-twitter btn-block" v-pml-tooltip="'Twitter'" target="_blank">
                    <i class="fa-fw fa fa-twitter"></i>
                </a>
            </div>
            <div class="icons" :class="{ 'disabled': !lead.pinterest }">
                <a :href="lead.pinterest" class="btn btn-pinterest btn-block" v-pml-tooltip="'Pinterest'" target="_blank">
                    <i class="fa-fw fa fa-pinterest"></i>
                </a>
            </div>
        </div>
    </div>
</template>

<script>
    import Notify from 'utils/helpers/notify'
    import {trans} from 'utils/helpers/translation'
    import {research} from 'services/leads'
    import {isNil} from 'lodash'
    import {mapGetters} from 'vuex'

    export default {
        name: 'LeadInfo',
        props: {
            'lead': {
                type: Object,
                required: true
            }
        },
        data () {
            return {
                isResearching: false
            }
        },
        computed: {
            ...mapGetters({
                'deal': 'getDealInfo',
                'tenant': 'getTenant'
            }),
            comments () {
                return this.$options.filters.pmlNl2br(this.lead.comments) || ''
            },
            mailto () {
                if (isNil(this.tenant.domain))
                    return `mailto:${this.lead.email}`

                return `mailto:${this.lead.email}?bcc=${this.tenant.domain}@pml.email`
            }
        },
        methods: {
            clearNumber: (number) => number.replace(/\D/g, ''),
            skypeLink (link) {
                return !isNil(link) && link !== '' ? `skype:${link}?call` : ''
            },
            leadResearch () {
                this.isResearching = true

                research(this.lead.id)
                    .then(response => {
                        if (response.id)
                            Notify.success(trans('lead_successfully_updated'))
                        else
                            Notify.error(trans(response))
                    })
                    .catch(error => Notify.error(trans(error.response.data)))
                    .then(() => {
                        this.isResearching = false
                    })
            }
        }
    }
</script>



// WEBPACK FOOTER //
// src/modules/leads/components/show/profile/Main.vue